import React, { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { UserApiContext } from "../../../../UserDetailsContext/UserDetailsContext";
import {
  downloadStandardFormat,
  isString,
  readExcelFile,
  trackUserUploadsMastersFileForBulkUploads,
} from "../../Utility/utility";
import { MyCompanyProfileContext } from "../../../../../MyCompanyProfileContext";
import { useMixPanelForTracking } from "../../../../../utils/TrackMixPanel";

const TM_HEADERS = [
  "Sr No.",
  "Truck Category*",
  "Truck Name",
  "Truck Type*",
  "Length*",
  "Breadth*",
  "Height*",
  "Volume*",
  "Radius",
  "Weight*",
  "Weight Unit*",
];

const requiredColumns = [
  "Truck Category*",
  "Truck Type*",
  "Length*",
  "Breadth*",
  "Height*",
  "Weight*",
  "Weight Unit*",
];

/**
 * Step 1 - UploadFile 
 * component for uploading and validating truck master upload files.
 * 
 * @param {number} step - current step in the upload process.
 * @param {React.SetStateAction} setStep - Function to set the current step.
 * @param {Array} uploadedData - The data uploaded from the file.
 * @param {React.SetStateAction} setUploadedData - Function to set the uploaded data.
 */
const UploadFile = ({
  setStep = "",
  setUploadedData,
  companyId,
}) => {
  const [errors, setErrors] = useState([]);
  const { userDetails } = useContext(UserApiContext);
  const fileInputRef = useRef(null);
  const [errorTypes, setErrorTypes] = useState({
    wrongFileType: false,
    headersMismatch: false,
    missingMandatoryFields: false,
    invalidNumericValues: false,
    invalidWeightUnit: false,
  });
  const { companySettingData } = useContext(MyCompanyProfileContext); 
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  /**
   * Function to validate excel data
   * @param {array} data
   * @returns {number}
   */
  const validateData = (data) => {
    const errors = [];
    let newErrorTypes = {};

    if (data.length === 0) {
      toast.info("Please Upload a valid File.");
      return false;
    }

    const numericColumns = ["Length*", "Breadth*", "Height*", "Weight*"];
    const validWeightUnits = ["KG", "MT"];

    const headers = Object.keys(data[0] || {});

    // Check if all required columns are present
    const missingColumns = requiredColumns.filter(
      (col) => !headers.includes(col)
    );

    if (missingColumns.length > 0) {
      errors.push(`Missing required columns: ${missingColumns.join(", ")}`);
      newErrorTypes.headersMismatch = true;
    }

    // Validate data rows
    data.forEach((row, i) => {
      requiredColumns.forEach((col) => {
        const value = row[col];
        if (value === undefined || value === "") {
          errors.push(`Row ${i + 2}: ${col} cannot be empty`);
          newErrorTypes.missingMandatoryFields = true;
        }
      });

      // validations for numeric columns
      numericColumns.forEach((col) => {
        const value = row[col];
        if (value !== undefined && value !== "" && isNaN(parseFloat(value))) {
          errors.push(`Row ${i + 2}: ${col} must be a number`);
          newErrorTypes.invalidNumericValues = true;
        }
      });

      const weightUnit = row["Weight Unit*"];
      if (isString(weightUnit) && !validWeightUnits.includes(weightUnit.toUpperCase())) {
          errors.push(`Row ${i + 2}: Weight Unit must be KG or MT`);
          newErrorTypes.invalidWeightUnit = true;
      } else if (!isString(weightUnit)) {
          errors.push(`Row ${i + 2}: Weight Unit must be a string`);
          newErrorTypes.invalidWeightUnit = true;
      }
    });

    setErrors(errors);
    setErrorTypes(newErrorTypes);
    return errors.length === 0;
  };

  /**
   * Handles the file upload, including validation.
   * @param {Event} e - The file input change event.
   */
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    resetFileInput();
    if (selectedFile) {
      resetErrors();

      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrorTypes((prev) => ({ ...prev, wrongFileType: true }));
        setErrors(["The file must be in .xlsx or .xls format."]);
        return;
      }

      const { data, error } = await readExcelFile(selectedFile);

      if (error) {
        toast.error(error);
        return;
      }

      if (validateData(data)) {
        setUploadedData(data);
        setStep(1);
      }

      trackUserUploadsMastersFileForBulkUploads(
        "Truck",
        userDetails,
        companyId,
        selectedFile.name,
        TrackMixPanel
      );

        TrackMixPanel({
          ModuleName: "masters",
          ScreenName: "truck master upload",
          Data: selectedFile.name,
          Definition: "user successfully uploaded truck master file",
        });
    }
  };

  /**
  * Reset file input, errors and error types 
  * @returns {void}
  */
  const handleDownloadTemplate = () => {
    downloadStandardFormat(TM_HEADERS, "TruckMaster", TrackMixPanel);
    TrackMixPanel({
      ModuleName: "masters",
      ScreenName: "truck master upload",
      Data: "truckMaster template",
      Definition: "user downloaded truck master template file",
    });
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
  * Reset file input, errors and error types 
  * @returns {void}
  */  
  const resetErrors = () => {
    setErrors([]);
    setErrorTypes({
      wrongFileType: false,
      headersMismatch: false,
      missingMandatoryFields: false,
      invalidNumericValues: false,
      invalidWeightUnit: false,
    });
  };

  return (
    <div className="card-body overflow-auto" style={{ maxHeight: "460px" }}>
      <div className="mb-4 text-center">
        <button
          className="btn btn-primary"
          onClick={() => downloadStandardFormat(TM_HEADERS, "TruckMaster")}
        >
          Download Standard Format
        </button>
      </div>
      <div className="alert alert-warning" style={{ margin: 0 }}>
        <h5 className="alert-heading under">
          <u>File Requirements</u>:
        </h5>
        <ul>
          <li
            style={{
              color: errorTypes.wrongFileType ? "red" : "inherit",
              fontWeight: errorTypes.wrongFileType ? "bold" : "normal",
            }}
          >
            The file must be in .xlsx or .xls format.
            {errorTypes.wrongFileType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.headersMismatch ? "red" : "inherit",
              fontWeight: errorTypes.headersMismatch ? "bold" : "normal",
            }}
          >
            Ensure that headers match the standard format.
            {errorTypes.headersMismatch && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.missingMandatoryFields ? "red" : "inherit",
              fontWeight: errorTypes.missingMandatoryFields ? "bold" : "normal",
            }}
          >
            Mandatory fields are marked with *:
            {errorTypes.missingMandatoryFields && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
            <ul>
              <li>
                {TM_HEADERS.filter((header) => header.endsWith("*")).map(
                  (header, index, array) => (
                    <React.Fragment key={index}>
                      <span>{header}</span>
                      {index < array.length - 1 && ", "}
                    </React.Fragment>
                  )
                )}
              </li>
            </ul>
          </li>
          <li
            style={{
              color: errorTypes.invalidNumericValues ? "red" : "inherit",
              fontWeight: errorTypes.invalidNumericValues ? "bold" : "normal",
            }}
          >
            Length, Breadth, Height, and Weight must be valid numbers.
            {errorTypes.invalidNumericValues && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidWeightUnit ? "red" : "inherit",
              fontWeight: errorTypes.invalidWeightUnit ? "bold" : "normal",
            }}
          >
            Weight Unit must be KG or MT.
            {errorTypes.invalidWeightUnit && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
        </ul>
      </div>
      <div className="text-center">
        <label className="btn btn-success upload-file-button-enquiry-my-account m-4">
          <input
            type="file"
            className="form-control d-none"
            id="upload_file_button"
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            ref={fileInputRef}
          />
          <i className="fas fa-upload mr-2"></i>Upload File
        </label>
      </div>
      {errors.length > 0 && (
        <div>
          <h6 className="text-danger">Errors:</h6>
          <ul className="list-group">
            {errors.map((error, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-danger"
              >
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadFile;
